import { Loader2 } from "lucide-react";
import { forwardRef } from "react";
import { cn } from "@/lib/utils";

type ButtonVariant = "primary" | "default" | "text" | "link" | "danger";

export interface ButtonProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "type"> {
	variant?: ButtonVariant;
	type?: ButtonVariant | "submit" | "reset" | "button";
	size?: "small" | "middle" | "large";
	loading?: boolean;
	icon?: React.ReactNode;
	danger?: boolean;
}

const isVariantType = (type: string): type is ButtonVariant => {
	return ["primary", "default", "text", "link", "danger"].includes(type);
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			type = "default",
			size = "middle",
			loading = false,
			icon,
			danger = false,
			disabled,
			children,
			...props
		},
		ref,
	) => {
		const effectiveButtonVariant: ButtonVariant = variant
			? variant
			: isVariantType(type)
				? type
				: "default";

		const actualHtmlType: "submit" | "reset" | "button" =
			!isVariantType(type) && (type === "submit" || type === "reset") ? type : "button";
		const isDisabled = disabled || loading;

		const effectiveVariant = danger ? "danger" : effectiveButtonVariant;

		const baseStyles =
			"inline-flex items-center justify-center gap-2 font-medium transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

		const sizeStyles = {
			small: "h-7 px-3 text-sm rounded",
			middle: "h-9 px-4 text-sm rounded-md",
			large: "h-11 px-6 text-base rounded-md",
		};

		const variantStyles = {
			primary:
				"bg-primary-500 text-white hover:bg-primary-600 active:bg-primary-700 border border-primary-500",
			default:
				"bg-white text-gray-700 hover:text-primary-500 hover:border-primary-500 border border-gray-300 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:border-primary-500",
			text: "bg-transparent text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-700 border-none",
			link: "bg-transparent text-primary-500 hover:text-primary-600 border-none underline-offset-4 hover:underline p-0 h-auto",
			danger:
				"bg-error-500 text-white hover:bg-error-600 active:bg-error-700 border border-error-500",
		};

		return (
			<button
				ref={ref}
				type={actualHtmlType}
				disabled={isDisabled}
				className={cn(baseStyles, sizeStyles[size], variantStyles[effectiveVariant], className)}
				{...props}
			>
				{loading ? (
					<Loader2 className="h-4 w-4 animate-spin" />
				) : (
					icon && <span className="flex-shrink-0">{icon}</span>
				)}
				{children && <span>{children}</span>}
			</button>
		);
	},
);

Button.displayName = "Button";

export { Button };
